import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { IconDownload, IconLoader2, IconInfoCircle } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { SearchParam } from '@common/ts/enums';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMutateDownloadAnalysisReport, useGetFilters } from '@/reactQuery/get';
import useTranslation from '@/hooks/useTranslation';
import downloadFile from '@/utils/downloadFile';
import { useCurrentProject } from '@/hooks/useCurrentProject';

interface DownloadAnalysisButtonProps {
  disabled?: boolean;
}

function DownloadAnalysisButton({ disabled = false }: DownloadAnalysisButtonProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const [isDownloading, setIsDownloading] = useState(false);

  const { currentProject } = useCurrentProject();
  const { data: filtersListData } = useGetFilters(projectId);
  const { mutateAsync: mutateAsyncDownloadAnalysisReport } = useMutateDownloadAnalysisReport();

  const getFilterNameById = (id: string | null): string => {
    if (filtersListData && id) {
      // Filter data from API includes _id field which isn't in the FiltersDataToSendProps type
      const filter = (filtersListData as unknown as Array<{ _id: string; name: string }>).find(
        (f) => f._id === id,
      );
      return filter?.name ?? '';
    }
    return '';
  };

  const filterName = getFilterNameById(filterId);

  const handleDownload = async () => {
    if (!projectId) return;

    setIsDownloading(true);
    try {
      const result = await mutateAsyncDownloadAnalysisReport({
        projectId,
        filterId,
      });

      // Construct filename with project title and optional filter name
      const projectTitle = currentProject?.title || 'analysis_report';
      const sanitizedProjectTitle = projectTitle
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '_');
      let filename: string;
      if (filterName) {
        const sanitizedFilterName = filterName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_');
        filename = `${sanitizedProjectTitle}_${sanitizedFilterName}_analysis.txt`;
      } else {
        filename = `${sanitizedProjectTitle}_analysis.txt`;
      }

      downloadFile(result.data, filename);
      toast.success(lang.get('msg.analysisReportSuccessfullyDownloaded'));
    } catch {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    } finally {
      setIsDownloading(false);
    }
  };

  const tooltipContent = filterId
    ? lang.get('msg.downloadAnalysisReportWithFilter').replace('{filterName}', filterName)
    : lang.get('msg.downloadAnalysisReportNoFilter');

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={disabled || isDownloading}
              className="flex items-center gap-2"
            >
              {isDownloading ? (
                <IconLoader2 className="h-4 w-4 animate-spin" />
              ) : (
                <IconDownload className="h-4 w-4" />
              )}
              {lang.get('msg.downloadAnalysisReport')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipContent}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help">
              <IconInfoCircle className="h-4 w-4 text-gray-400" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{lang.get('msg.downloadAnalysisReportInfo')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default DownloadAnalysisButton;
